/**
 * Horário fixo — "o João vem toda terça às 17h".
 *
 * Quatro peças, cada uma onde a frase é dita:
 *
 *  - a **seção da ficha**, porque é com o cliente na cadeira que ele diz "me
 *    deixa fixo na terça";
 *  - o **"Repetir"** na folha do atendimento, que monta a série a partir do
 *    horário que acabou de ser marcado;
 *  - a **folha da série**, onde ele vê as próximas datas, muda a regra e
 *    encerra;
 *  - o **aviso de datas puladas** em Pedidos: uma terça que caiu num feriado
 *    não pode ficar escondida dentro da ficha de alguém.
 *
 * Nenhuma tela aqui pergunta tempo. A série guarda **quais** serviços, e cada
 * data leva o que a ficha do cliente manda — quem quer outro tempo corrige na
 * ficha, e a correção vale também fora do horário fixo.
 */

import { zonedMinutesToInstant } from '@barbearia/core';
import type {
  ClientView,
  SeriesChangeScope,
  SeriesOutcomeView,
  SeriesSkipView,
  SeriesView,
  ServiceView,
} from '@barbearia/shared';
import {
  describeRecurrence,
  describeSeriesOutcome,
  describeServices,
  SERIES_INTERVAL_OPTIONS,
  SKIP_REASON_LABELS,
  WEEKDAY_SHORT,
} from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  ErrorNote,
  Field,
  OptionList,
  Sheet,
  Spinner,
} from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import {
  dayAndMonth,
  duration,
  parseHhMm,
  relativeDay,
  timeOf,
  todayIn,
  weekdayOf,
} from '../../lib/format.ts';

const INTERVAL_LABELS: Record<number, string> = {
  1: 'Toda semana',
  2: 'Semana sim, semana não',
};

function intervalLabel(weeks: number): string {
  return INTERVAL_LABELS[weeks] ?? `A cada ${weeks} semanas`;
}

function hhmm(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}

/** "ter, 14/10 · 17:00" — uma data da série numa linha. */
function occurrenceLabel(startsAt: string, timezone: string, today: string): string {
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date(startsAt));
  return `${relativeDay(date, today, timezone)} · ${timeOf(startsAt, timezone)}`;
}

/** Depois de qualquer mudança: a agenda, os pedidos e as próprias séries. */
function useRefreshSeries() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['series'] }),
      queryClient.invalidateQueries({ queryKey: ['agenda'] }),
      queryClient.invalidateQueries({ queryKey: ['overview'] }),
    ]);
  };
}

function messageOf(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : 'Não deu certo';
}

// ── Formulário ──────────────────────────────────────────────────────────────

/** O que dá para pré-preencher — vindo de um atendimento ou da série que se edita. */
export interface SeriesDraft {
  serviceIds: string[];
  weekdays: number[];
  startMinute: number;
  intervalWeeks: number;
  startDate: string;
  endDate: string | null;
  notes?: string | null;
}

/**
 * Montar ou mudar a regra.
 *
 * Mudando uma série que já tem datas marcadas, salvar faz **uma pergunta**:
 * mudar também as que já estão na agenda, ou só valer daqui pra frente. Não há
 * resposta certa para o sistema escolher sozinho — a quarta que vira quinta
 * pode ser o que ele quer para todo mundo, ou só a partir do mês que vem.
 */
export function SeriesFormSheet({
  clientId,
  clientName,
  draft,
  series,
  fromAppointmentId,
  onClose,
}: {
  clientId: string;
  clientName: string;
  draft?: SeriesDraft;
  /** Presente = editando esta série. */
  series?: SeriesView;
  fromAppointmentId?: string;
  onClose: () => void;
}) {
  const { tenant } = useTenant();
  const refresh = useRefreshSeries();
  const today = todayIn(tenant.timezone);

  const initial: SeriesDraft =
    draft ??
    (series
      ? {
          serviceIds: series.services.map((service) => service.id),
          weekdays: series.weekdays,
          startMinute: series.startMinute,
          intervalWeeks: series.intervalWeeks,
          startDate: series.startDate < today ? today : series.startDate,
          endDate: series.endDate,
          notes: series.notes,
        }
      : {
          serviceIds: [],
          weekdays: [],
          startMinute: 17 * 60,
          intervalWeeks: 1,
          startDate: today,
          endDate: null,
        });

  const [serviceIds, setServiceIds] = useState(initial.serviceIds);
  const [weekdays, setWeekdays] = useState(initial.weekdays);
  const [time, setTime] = useState(hhmm(initial.startMinute));
  const [intervalWeeks, setIntervalWeeks] = useState(initial.intervalWeeks);
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState<string | null>(initial.endDate);
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  /** Editando com datas já marcadas: a pergunta que salvar faz. */
  const [asking, setAsking] = useState(false);
  const [outcome, setOutcome] = useState<SeriesOutcomeView | null>(null);

  const { data } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
  });
  const services = (data?.services ?? []).filter(
    (service) => service.active || serviceIds.includes(service.id),
  );

  // O tempo ao lado de cada serviço é o deste cliente, não o do cadastro: é
  // ele que cada data da série vai ocupar na agenda.
  const { data: clientData } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.get<{ client: ClientView }>(`/clients/${clientId}`),
  });
  const ownMinutes = new Map(
    (clientData?.client.durations ?? []).map((entry) => [entry.serviceId, entry.minutes]),
  );

  const startMinute = parseHhMm(time);
  const rule = {
    serviceIds,
    weekdays: [...weekdays].sort(),
    startMinute,
    intervalWeeks,
    startDate,
    endDate,
    notes: notes.trim() || undefined,
  };

  const save = useMutation({
    mutationFn: (scope?: SeriesChangeScope) =>
      series
        ? api.patch<SeriesOutcomeView>(`/series/${series.id}`, { ...rule, scope })
        : api.post<SeriesOutcomeView>('/series', { ...rule, clientId, fromAppointmentId }),
    onSuccess: async (result) => {
      setAsking(false);
      setOutcome(result);
      await refresh();
    },
    onError: (caught) => {
      setAsking(false);
      setError(messageOf(caught));
    },
  });

  // Só pergunta quando há o que perguntar: sem datas futuras que acompanhariam
  // a regra, as duas respostas dão no mesmo.
  const followers = series?.upcoming.filter((item) => !item.detached).length ?? 0;
  const ready = serviceIds.length > 0 && weekdays.length > 0 && time !== '';

  const submit = () => {
    setError(null);
    if (series && followers > 0) setAsking(true);
    else save.mutate(series ? 'ALL_FUTURE' : undefined);
  };

  if (outcome) {
    return (
      <Sheet
        open
        onClose={onClose}
        title={outcome.series.label}
        footer={
          <div className="pb-1">
            <Button full onClick={onClose}>
              Pronto
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-[15px] text-ink">{describeSeriesOutcome(outcome)}</p>
          {outcome.series.upcoming[0] && (
            <p className="text-[14px] text-ink-soft">
              Próxima:{' '}
              {occurrenceLabel(outcome.series.upcoming[0].startsAt, tenant.timezone, today)}
            </p>
          )}
          <SkipList skips={outcome.series.skips} />
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={series ? 'Mudar horário fixo' : 'Horário fixo'}
      footer={
        asking ? (
          <div className="flex flex-col gap-2 pb-1">
            <p className="text-[14px] text-ink">
              {followers === 1
                ? 'A próxima data já está'
                : `As próximas ${followers} datas já estão`}{' '}
              na agenda. O que fazer com {followers === 1 ? 'ela' : 'elas'}?
            </p>
            <Button full loading={save.isPending} onClick={() => save.mutate('ALL_FUTURE')}>
              Mudar também as já marcadas
            </Button>
            <Button
              variant="secondary"
              full
              loading={save.isPending}
              onClick={() => save.mutate('ONLY_NEW')}
            >
              Manter; mudar só depois delas
            </Button>
            <Button variant="ghost" full onClick={() => setAsking(false)}>
              Voltar
            </Button>
          </div>
        ) : (
          <div className="pb-1">
            <Button full disabled={!ready} loading={save.isPending} onClick={submit}>
              {!ready
                ? serviceIds.length === 0
                  ? 'Escolha o serviço'
                  : 'Escolha o dia'
                : series
                  ? 'Salvar'
                  : 'Criar horário fixo'}
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-[14px] text-ink-soft">
          As datas entram na agenda de {clientName} com antecedência, cada uma com o tempo que a
          ficha manda. Dá para mexer em qualquer uma sem desfazer o resto.
        </p>

        <section>
          <SectionTitle>Dia da semana</SectionTitle>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_SHORT.map((label, day) => {
              const active = weekdays.includes(day);
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={active}
                  onClick={() =>
                    setWeekdays((current) =>
                      active ? current.filter((entry) => entry !== day) : [...current, day],
                    )
                  }
                  className={`h-11 w-11 rounded-lg text-[13px] font-medium ${
                    active
                      ? 'bg-primary text-primary-ink'
                      : 'border border-line bg-surface-raised text-ink-soft'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </section>

        <label className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Horário
          <input
            type="time"
            value={time}
            step={300}
            onChange={(event) => setTime(event.target.value)}
            className="mt-2 block w-full touch-target rounded-xl border border-line bg-surface-raised px-3 text-[16px] font-normal tracking-normal text-ink normal-case"
          />
        </label>

        <section>
          <SectionTitle>Frequência</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {SERIES_INTERVAL_OPTIONS.map((weeks) => (
              <Chip
                key={weeks}
                active={intervalWeeks === weeks}
                onClick={() => setIntervalWeeks(weeks)}
              >
                {intervalLabel(weeks)}
              </Chip>
            ))}
          </div>
        </section>

        <section>
          <SectionTitle>Serviços</SectionTitle>
          <OptionList
            items={services}
            selected={serviceIds}
            onSelect={(service) =>
              setServiceIds((current) =>
                current.includes(service.id)
                  ? current.filter((id) => id !== service.id)
                  : [...current, service.id],
              )
            }
            getKey={(service) => service.id}
            renderTitle={(service) => service.name}
            renderMeta={(service) =>
              duration(ownMinutes.get(service.id) ?? service.defaultDurationMinutes)
            }
          />
        </section>

        <div className="flex gap-3">
          <label className="flex-1 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Começa em
            <input
              type="date"
              value={startDate}
              min={today}
              onChange={(event) => event.target.value && setStartDate(event.target.value)}
              className="mt-2 block w-full touch-target rounded-xl border border-line bg-surface-raised px-3 text-[16px] font-normal tracking-normal text-ink normal-case"
            />
          </label>
          <label className="flex-1 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Até
            <input
              type="date"
              value={endDate ?? ''}
              min={startDate}
              onChange={(event) => setEndDate(event.target.value || null)}
              className="mt-2 block w-full touch-target rounded-xl border border-line bg-surface-raised px-3 text-[16px] font-normal tracking-normal text-ink normal-case"
            />
          </label>
        </div>
        <p className="-mt-3 px-1 text-[12px] text-ink-faint">
          {endDate ? `Última data: ${dayAndMonth(endDate)}.` : 'Sem data para acabar.'}
          {endDate && (
            <button
              type="button"
              onClick={() => setEndDate(null)}
              className="touch-target ml-2 text-accent"
            >
              Tirar o fim
            </button>
          )}
        </p>

        <Field
          label="Observação (opcional)"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Vem direto do trabalho"
        />

        {weekdays.length > 0 && time && (
          <p className="px-1 text-[13px] text-ink-faint">
            Fica como:{' '}
            <span className="text-ink">
              {describeRecurrence({ weekdays, startMinute, intervalWeeks })}
            </span>
          </p>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}
      </div>
    </Sheet>
  );
}

// ── Folha da série ──────────────────────────────────────────────────────────

/**
 * A série aberta: as próximas datas, as que ficaram de fora e o que dá para
 * fazer com ela.
 *
 * Encerrar pergunta o que fazer com o que já está marcado, pelo mesmo motivo
 * que mudar a regra pergunta: "o João não vem mais" e "a partir do mês que vem
 * ele muda de dia" pedem respostas opostas para as mesmas datas.
 */
export function SeriesSheet({ seriesId, onClose }: { seriesId: string; onClose: () => void }) {
  const { tenant } = useTenant();
  const refresh = useRefreshSeries();
  const today = todayIn(tenant.timezone);

  const [editing, setEditing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['series', 'detail', seriesId],
    queryFn: () => api.get<{ series: SeriesView }>(`/series/${seriesId}`),
  });

  const end = useMutation({
    mutationFn: (cancelFuture: boolean) => api.post(`/series/${seriesId}/end`, { cancelFuture }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
    onError: (caught) => setError(messageOf(caught)),
  });

  const series = data?.series;
  const marked = series?.upcoming.filter((item) => !item.detached).length ?? 0;
  const handMade = (series?.upcoming.length ?? 0) - marked;

  return (
    <Sheet
      open
      onClose={onClose}
      title={series?.label ?? 'Horário fixo'}
      footer={
        series && !series.endedAt ? (
          ending ? (
            <div className="flex flex-col gap-2 pb-1">
              <p className="text-[14px] text-ink">
                {marked > 0
                  ? `${marked === 1 ? 'Uma data já está marcada' : `${marked} datas já estão marcadas`}. Cancelar ${marked === 1 ? 'ela' : 'elas'} também?`
                  : 'Nenhuma data marcada para cancelar. A série só para de gerar.'}
              </p>
              {marked > 0 && (
                <Button
                  variant="danger"
                  full
                  loading={end.isPending}
                  onClick={() => end.mutate(true)}
                >
                  Encerrar e cancelar as marcadas
                </Button>
              )}
              <Button
                variant="secondary"
                full
                loading={end.isPending}
                onClick={() => end.mutate(false)}
              >
                {marked > 0 ? 'Encerrar e manter as marcadas' : 'Encerrar'}
              </Button>
              {handMade > 0 && (
                <p className="px-1 text-[12px] text-ink-faint">
                  {handMade === 1
                    ? 'A data que você mudou à mão continua marcada.'
                    : `As ${handMade} datas que você mudou à mão continuam marcadas.`}
                </p>
              )}
              <Button variant="ghost" full onClick={() => setEnding(false)}>
                Voltar
              </Button>
            </div>
          ) : (
            <div className="flex gap-2 pb-1">
              <Button variant="secondary" className="flex-1" onClick={() => setEditing(true)}>
                Mudar
              </Button>
              <Button variant="danger" className="flex-1" onClick={() => setEnding(true)}>
                Encerrar
              </Button>
            </div>
          )
        ) : null
      }
    >
      {isPending && <Spinner />}

      {series && (
        <div className="flex flex-col gap-5">
          <dl className="overflow-hidden rounded-xl border border-line">
            <Row label="Cliente" value={series.client.name} />
            <Row
              label={series.services.length > 1 ? 'Serviços' : 'Serviço'}
              value={describeServices(series.services.map((service) => service.name))}
            />
            <Row label="Profissional" value={series.staff.name} />
            <Row
              label="Até"
              value={series.endDate ? dayAndMonth(series.endDate) : 'Sem data para acabar'}
            />
          </dl>

          {series.notes && <p className="px-1 text-[14px] text-ink-soft">“{series.notes}”</p>}

          <SkipList skips={series.skips} />

          <section>
            <SectionTitle>Próximas datas</SectionTitle>
            {series.upcoming.length === 0 ? (
              <p className="px-1 text-[13px] text-ink-faint">Nenhuma data marcada adiante.</p>
            ) : (
              <ul className="overflow-hidden rounded-xl border border-line">
                {series.upcoming.map((item) => (
                  <li
                    key={item.appointmentId}
                    className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3 last:border-b-0"
                  >
                    <span className="text-[15px] text-ink tabular-nums">
                      {occurrenceLabel(item.startsAt, tenant.timezone, today)}
                    </span>
                    {item.detached && <Badge>mudada à mão</Badge>}
                  </li>
                ))}
              </ul>
            )}
            <p className="px-1 pt-2 text-[12px] text-ink-faint">
              Para mudar ou cancelar uma data só, abra ela na agenda. A série não volta a marcar o
              que você mexeu.
            </p>
          </section>

          {error && <ErrorNote>{error}</ErrorNote>}

          {editing && (
            <SeriesFormSheet
              clientId={series.client.id}
              clientName={series.client.name}
              series={series}
              onClose={() => setEditing(false)}
            />
          )}
        </div>
      )}
    </Sheet>
  );
}

// ── Ficha do cliente ────────────────────────────────────────────────────────

/** Os horários fixos de um cliente, dentro da ficha dele. */
export function ClientSeriesSection({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}) {
  const { tenant } = useTenant();
  const today = todayIn(tenant.timezone);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['series', 'client', clientId],
    queryFn: () => api.get<{ series: SeriesView[] }>('/series', { clientId }),
  });

  const list = data?.series ?? [];

  return (
    <section>
      <SectionTitle>Horário fixo</SectionTitle>

      {list.length > 0 && (
        <ul className="mb-2 flex flex-col gap-2">
          {list.map((series) => (
            <li key={series.id}>
              <button
                type="button"
                onClick={() => setOpen(series.id)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-line bg-surface-raised px-4 py-3 text-left active:bg-surface-sunken"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-medium text-ink">
                    ↻ {series.label}
                  </span>
                  <span className="block truncate text-[13px] text-ink-faint">
                    {series.upcoming[0]
                      ? `Próxima: ${occurrenceLabel(series.upcoming[0].startsAt, tenant.timezone, today)}`
                      : series.startDate > today
                        ? `Começa ${dayAndMonth(series.startDate)}`
                        : 'Nenhuma data marcada adiante'}
                  </span>
                </span>
                {series.skips.length > 0 && (
                  <Badge tone="warn">
                    {series.skips.length} {series.skips.length === 1 ? 'pulada' : 'puladas'}
                  </Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Button variant="secondary" full onClick={() => setCreating(true)}>
        {list.length > 0 ? 'Outro horário fixo' : 'Deixar horário fixo'}
      </Button>

      {creating && (
        <SeriesFormSheet
          clientId={clientId}
          clientName={clientName}
          onClose={() => setCreating(false)}
        />
      )}
      {open && <SeriesSheet seriesId={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

// ── Datas puladas ───────────────────────────────────────────────────────────

/**
 * As datas que a série não marcou, em Pedidos.
 *
 * Ficam junto dos pedidos porque são a mesma coisa para o barbeiro: alguém
 * esperando uma resposta dele. Some sozinho quando não há nada.
 */
export function SeriesSkipsSection() {
  const { data } = useQuery({
    queryKey: ['series', 'skips'],
    queryFn: () => api.get<{ skips: SeriesSkipView[] }>('/series/skips'),
  });

  const skips = data?.skips ?? [];
  if (skips.length === 0) return null;

  return (
    <section className="px-4 pt-6">
      <SkipList skips={skips} withClient />
    </section>
  );
}

function SkipList({ skips, withClient }: { skips: SeriesSkipView[]; withClient?: boolean }) {
  if (skips.length === 0) return null;

  return (
    <div>
      <SectionTitle>Horário fixo que ficou de fora</SectionTitle>
      <ul className="flex flex-col gap-2">
        {skips.map((skip) => (
          <SkipRow key={`${skip.seriesId}-${skip.date}`} skip={skip} withClient={withClient} />
        ))}
      </ul>
    </div>
  );
}

/**
 * Uma data pulada e as três saídas dela: encaixar no horário de sempre, marcar
 * em outro, ou dispensar ("é feriado, ele sabe").
 */
function SkipRow({ skip, withClient }: { skip: SeriesSkipView; withClient?: boolean }) {
  const { tenant } = useTenant();
  const refresh = useRefreshSeries();

  const [other, setOther] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ startsAt: string; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const book = useMutation({
    mutationFn: (input: { startsAt: string; acceptConflict: boolean }) =>
      api.post(`/series/${skip.seriesId}/skips/${skip.date}/book`, input),
    onSuccess: refresh,
    onError: (caught, input) => {
      // O sistema avisa, não proíbe: o conflito volta para ele decidir.
      if (caught instanceof ApiError && caught.status === 409 && !input.acceptConflict) {
        setConflict({ startsAt: input.startsAt, message: caught.message });
      } else {
        setError(messageOf(caught));
      }
    },
  });

  const dismiss = useMutation({
    mutationFn: () => api.post(`/series/${skip.seriesId}/skips/${skip.date}/dismiss`),
    onSuccess: refresh,
    onError: (caught) => setError(messageOf(caught)),
  });

  const at = (time: string) =>
    zonedMinutesToInstant(skip.date, parseHhMm(time), tenant.timezone).toISOString();

  const ruleTime = timeOf(skip.startsAt, tenant.timezone);

  return (
    <li className="rounded-2xl border border-warn/40 bg-warn/5 px-4 py-3">
      {withClient && <p className="text-[16px] font-semibold text-ink">{skip.client.name}</p>}
      <p className="text-[15px] font-medium text-ink">
        {weekdayOf(skip.startsAt, tenant.timezone)}, {dayAndMonth(skip.date)} ·{' '}
        <span className="tabular-nums">{ruleTime}</span>
      </p>
      <p className="text-[13px] text-ink-soft">
        {skip.reason
          ? `Não foi marcada: ${SKIP_REASON_LABELS[skip.reason]}${
              skip.reason === 'OVERLAP' && skip.overlapMinutes > 0
                ? ` (${skip.overlapMinutes} min)`
                : ''
            }.`
          : 'O horário vagou — dá para marcar agora.'}
        {withClient && <span className="text-ink-faint"> {skip.label}.</span>}
      </p>

      {conflict ? (
        <div className="mt-3 flex flex-col gap-2">
          <p className="text-[14px] text-warn">{conflict.message}.</p>
          <Button
            full
            loading={book.isPending}
            onClick={() => book.mutate({ startsAt: conflict.startsAt, acceptConflict: true })}
          >
            Encaixar mesmo assim
          </Button>
          <Button variant="ghost" full onClick={() => setConflict(null)}>
            Voltar
          </Button>
        </div>
      ) : other !== null ? (
        <div className="mt-3 flex items-end gap-2">
          <label className="flex-1 text-[13px] text-ink-soft">
            Outro horário
            <input
              type="time"
              value={other}
              step={300}
              onChange={(event) => setOther(event.target.value)}
              className="mt-1 block w-full touch-target rounded-lg border border-line bg-surface px-3 text-[16px] text-ink"
            />
          </label>
          <Button
            loading={book.isPending}
            disabled={!other}
            onClick={() => book.mutate({ startsAt: at(other), acceptConflict: false })}
          >
            Marcar
          </Button>
          <Button variant="ghost" onClick={() => setOther(null)}>
            Voltar
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <Button
            full
            loading={book.isPending}
            onClick={() =>
              // Sem motivo, cabe: marca direto. Com motivo, ele já leu qual é
              // e tocou mesmo assim — perguntar de novo seria só atrito.
              book.mutate({ startsAt: skip.startsAt, acceptConflict: skip.reason !== null })
            }
          >
            {skip.reason ? `Marcar mesmo assim às ${ruleTime}` : `Marcar às ${ruleTime}`}
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setOther(ruleTime)}>
              Outro horário
            </Button>
            <Button
              variant="ghost"
              className="flex-1"
              loading={dismiss.isPending}
              onClick={() => dismiss.mutate()}
            >
              Dispensar
            </Button>
          </div>
        </div>
      )}

      {error && <p className="pt-2 text-[13px] text-danger">{error}</p>}
    </li>
  );
}

// ── Peças ───────────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
      {children}
    </h3>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`touch-target rounded-xl border px-4 text-[14px] font-medium ${
        active
          ? 'border-primary bg-primary/10 text-ink'
          : 'border-line bg-surface-raised text-ink-soft'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-2.5 last:border-b-0">
      <dt className="text-[13px] text-ink-faint">{label}</dt>
      <dd className="truncate text-[14px] font-medium text-ink">{value}</dd>
    </div>
  );
}
